import {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardTitle,
  CollapsibleCardTrigger,
} from '@heidi/design-system/collapsible-card';
import { Item, ItemContent, ItemGroup, ItemTitle } from '@heidi/design-system/item';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { focusRing } from '@heidi/design-system/styles';
import { cn } from '@heidi/utils';
import { useMemo, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { WorkflowRun } from '../../../contracts';
import { formatRunSidebarLabel } from '../../../data/workflows/components/formatRunSidebarLabel';
import { sortRunsNewestFirst } from '../../../data/workflows/components/routineOverviewUtils';
import { RoutineRunRow } from './RoutineRunRow';

/** Quiet in-card footer row — "See all" / "Show less". */
function RoutineRunHistoryToggleRow({
  label,
  onClick,
}: {
  label: ReactNode;
  onClick: () => void;
}): JSX.Element {
  return (
    <Item asChild size="sm">
      <button
        type="button"
        onClick={onClick}
        className={cn('justify-center text-text-tertiary', focusRing)}
      >
        <ItemContent className="flex-none items-center">
          <ItemTitle>{label}</ItemTitle>
        </ItemContent>
      </button>
    </Item>
  );
}

export interface RoutineRecentRunsRailCardProps {
  /** Rows shown before "See all" — typically the newest five. */
  runs: WorkflowRun[];
  /** Full history, fetched when the clinician expands "See all". */
  allRuns?: WorkflowRun[];
  /** Whether more runs exist than {@link runs} lists. */
  hasMore: boolean;
  selectedProjectId: string | null;
  onRunClick: (projectId: string | null | undefined) => void;
  /** Controlled "See all" expansion. */
  historyExpanded: boolean;
  onHistoryExpandedChange: (expanded: boolean) => void;
  /** Hosted in a surface that already carries chrome (the header popover). */
  standalone?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Presentational Recent-runs card — {@link CollapsibleCard} chrome + run rows.
 * Data fetching and rail visibility live in {@link RoutineRecentRunsRail}.
 */
export function RoutineRecentRunsRailCard({
  runs,
  allRuns,
  hasMore,
  selectedProjectId,
  onRunClick,
  historyExpanded,
  onHistoryExpandedChange,
  standalone = false,
  className,
  'data-testid': dataTestId = 'routine-recent-runs-rail',
}: RoutineRecentRunsRailCardProps): JSX.Element {
  const intl = useIntl();
  const [bodyOpen, setBodyOpen] = useState(true);

  const sortedAllRuns = useMemo(() => sortRunsNewestFirst(allRuns ?? []), [allRuns]);
  const displayRuns = historyExpanded && sortedAllRuns.length > 0 ? sortedAllRuns : runs;

  const rowElements = displayRuns.map((run) => (
    <RoutineRunRow
      key={run.agent_run_id ?? run.project_id}
      appearance="card"
      label={run.created_at ? formatRunSidebarLabel(run.created_at, intl) : ''}
      selected={Boolean(selectedProjectId && run.project_id === selectedProjectId)}
      onClick={() => onRunClick(run.project_id)}
    />
  ));

  const rowList =
    historyExpanded && sortedAllRuns.length > 0 ? (
      <ScrollArea type="hover" className="max-h-[60vh]">
        <div className="flex flex-col">{rowElements}</div>
      </ScrollArea>
    ) : (
      rowElements
    );

  return (
    <CollapsibleCard
      open={bodyOpen}
      onOpenChange={setBodyOpen}
      variant={standalone ? 'flat' : 'default'}
      data-testid={dataTestId}
      className={cn('w-72', className)}
    >
      <CollapsibleCardTrigger>
        <CollapsibleCardTitle>
          <FormattedMessage
            defaultMessage="Recent runs"
            id="46nn//xGIY"
            description="Heading of the recent-runs card beside a routine run chat"
          />
        </CollapsibleCardTitle>
      </CollapsibleCardTrigger>
      <CollapsibleCardContent>
        <ItemGroup className="gap-0 has-[[data-size=sm]]:gap-0">
          {rowList}
          {!historyExpanded && hasMore ? (
            <RoutineRunHistoryToggleRow
              label={
                <FormattedMessage
                  defaultMessage="See all"
                  id="ClHtmalIwD"
                  description="Recent-runs card footer: expand the card into the routine's full run history"
                />
              }
              onClick={() => onHistoryExpandedChange(true)}
            />
          ) : null}
          {historyExpanded ? (
            <RoutineRunHistoryToggleRow
              label={
                <FormattedMessage
                  defaultMessage="Show less"
                  id="C4R2U7oQqh"
                  description="Recent-runs card footer: collapse the expanded run history"
                />
              }
              onClick={() => onHistoryExpandedChange(false)}
            />
          ) : null}
        </ItemGroup>
      </CollapsibleCardContent>
    </CollapsibleCard>
  );
}
